import { OwnerAffordability, ownerShortfallBlock } from '@lib/utils/ownerAffordability';

export type UploadDisabledState = {
  value: boolean;
  reason: string;
};

export type UploadDisabledOptions = {
  /**
   * True when the backend will charge this library's owner rather than the current user (see
   * `resolveBillingOwnership`). Both local balance checks are skipped in that case:
   * `creditsAfterProcessing` and `storageLeftAfterProcessing` are computed from `billingStore`,
   * i.e. the *uploader's* wallet, so gating on them blocks an upload the payer can well afford.
   *
   * What replaces them is `ownerAffordability` below, and only when it has an answer.
   */
  billedToAnotherAccount?: boolean;
  /**
   * What replaces the two local figures above when another account pays. Omitting it, like every
   * state but `insufficient`, leaves the button as it was — see `ownerAffordability.ts`.
   */
  ownerAffordability?: OwnerAffordability;
  /** The owner's display name, when the backend gave one — only used to word the reason above. */
  ownerName?: string;
};

export const getUploadDisabledState = (
  creditsAfterProcessing: number | null,
  storageLeftAfterProcessing: number | null,
  nSelectedVideos: number,
  isUploading: boolean,
  { billedToAnotherAccount = false, ownerAffordability, ownerName }: UploadDisabledOptions = {},
): UploadDisabledState => {
  // Only the balance gates depend on who pays; the selection and in-flight gates below are
  // about this upload and always apply.
  if (billedToAnotherAccount) {
    const block = ownerShortfallBlock(billedToAnotherAccount, ownerAffordability, ownerName);
    if (block) {
      return { value: true, reason: block.reason };
    }
  } else {
    if (creditsAfterProcessing === null || creditsAfterProcessing <= 0) {
      return {
        value: true,
        reason: 'Not enough credits. Please top up or select fewer media to add.',
      };
    }

    if (storageLeftAfterProcessing === null || storageLeftAfterProcessing <= 0) {
      return {
        value: true,
        reason:
          'Not enough storage space. Please delete some media, upgrade your plan, or select fewer media to add.',
      };
    }
  }

  if (nSelectedVideos === 0) {
    return {
      value: true,
      reason: 'Please select at least one media to add to your library.',
    };
  }

  if (isUploading) {
    return {
      value: true,
      reason: 'Already adding media...',
    };
  }

  return {
    value: false,
    reason: 'Add media to library.',
  };
};
